import { AnalyticsEvent } from '../service/analytics-events'
import { analyticsService } from '../service/analytics-service'
import { authService } from '../service/auth/auth-service'
import { errorUtil } from '../util/auth-error-util'
import { queryKeys } from '../util/query-keys'
import { useMutation } from '@tanstack/react-query'
import { useRef } from 'react'

type VerifyPasswordlessLoginInput = {
  email: string
  code: string
}

export const useVerifyPasswordlessLogin = (options?: {
  onError?: (error: Error) => void
}): {
  verifyPasswordlessLogin: (input: VerifyPasswordlessLoginInput) => void
  isLoading: boolean
  error: Error | null
} => {
  // Submissions on this screen, so `otp_verified` can say how many tries it took.
  const attemptsRef = useRef(0)

  const {
    mutate: verifyPasswordlessLoginMutation,
    isLoading,
    error,
  } = useMutation<void, Error, VerifyPasswordlessLoginInput>(
    async (input) => {
      attemptsRef.current += 1
      await authService.verifyPasswordlessLogin({
        email: input.email,
        code: input.code,
      })
    },
    {
      mutationKey: queryKeys.verifyPasswordlessLogin(),
      onSuccess: () => {
        analyticsService.track(AnalyticsEvent.OTP_VERIFIED, { attempts: attemptsRef.current })
      },
      onError: (err) => {
        analyticsService.track(AnalyticsEvent.OTP_FAILED, { reason: err.message, attempts: attemptsRef.current })
        errorUtil.logError(err)
        options?.onError?.(err)
      },
    },
  )

  const verifyPasswordlessLogin = (input: VerifyPasswordlessLoginInput): void => {
    verifyPasswordlessLoginMutation(input)
  }

  return {
    verifyPasswordlessLogin,
    isLoading,
    error,
  }
}
